import React, { lazy, useEffect, useState } from "react"
import { Segmented } from "antd"
import { useNavigate, useSearchParams } from "react-router-dom"

import { useProjectStore } from "stores/projectStore"
import {
  ProvenanceSection,
  editorHistoryLink,
  isProvenanceSection,
} from "features/Provenance/utils/provenanceLinks"
import VersionHistoryPanel from "features/Provenance/components/VersionHistoryPanel.component"
import StampAndDrift from "features/Provenance/components/StampAndDrift.component"
import WaveCompare from "features/Provenance/components/WaveCompare.component"
import TrackerManager from "features/Provenance/components/TrackerManager.component"
import ProjectLineage from "features/Provenance/components/ProjectLineage.component"
import { GraphSuspense } from "features/Provenance/components/GraphSuspense.component"

// The ancestry graph loads the graph library, so it loads when the section is first opened
const ProjectAncestryView = lazy(
  () => import("features/Provenance/components/ProjectAncestry.component")
)

/**
 * Provenance tab: the project-level home for the compliance/provenance story. Sections:
 *  - Timeline: the project history (versions, imports, links, exports) and its check.
 *  - Version and data check: the current version ID and what it is made from, and the
 *    differences between the questionnaire and the data.
 *  - Compare: wave-over-wave comparison (IDs and field-level changes).
 *  - Tracker: the group of projects treated as waves of one study.
 *  - Lineage: a saved query's derivation graph, rebuilt on the server (the live-session view
 *    stays in Data Reporting).
 *  - Ancestry: which PROJECTS this one was built from (merges, wave forks), walked
 *    transitively. Lineage is variable-level within one file; this is project-level across
 *    files.
 *
 * `?section=<name>` in the URL opens that section (a link from another tab, see
 * provenanceLink), and a later change to it while the tab is open switches to it
 * too. The name leaves the URL once the user picks a section, as `?tab=` does.
 */
const ProvenanceTab: React.FC = () => {
  const project = useProjectStore((s) => s.project)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [section, setSection] = useState<ProvenanceSection>(() => {
    const asked = searchParams.get("section")
    return isProvenanceSection(asked) ? asked : "timeline"
  })

  useEffect(() => {
    const asked = searchParams.get("section")
    if (isProvenanceSection(asked)) setSection(asked)
  }, [searchParams])

  const pickSection = (next: ProvenanceSection) => {
    setSection(next)
    if (!searchParams.has("section")) return
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.delete("section")
        return params
      },
      { replace: true }
    )
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white">
      <div className="border-b border-slate-200 px-4 pt-3 pb-2">
        <Segmented
          value={section}
          onChange={(v) => pickSection(v as ProvenanceSection)}
          options={[
            { label: "Timeline", value: "timeline" },
            { label: "Version and data check", value: "stamp" },
            { label: "Compare", value: "compare" },
            { label: "Tracker", value: "trackers" },
            { label: "Lineage", value: "lineage" },
            { label: "Ancestry", value: "ancestry" },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1">
        {section === "timeline" ? (
          <VersionHistoryPanel
            inline
            isVisible
            projectId={project.id}
            // Restore lives in the editor (unsaved-changes guard, undo stack): deep-link there.
            onOpenInEditor={(seq) => navigate(editorHistoryLink(project.id, seq))}
            onViewAncestry={() => pickSection("ancestry")}
          />
        ) : section === "stamp" ? (
          <div className="h-full overflow-y-auto">
            <StampAndDrift projectId={project.id} projectName={project.Name} />
          </div>
        ) : section === "compare" ? (
          <div className="h-full overflow-y-auto">
            <WaveCompare projectId={project.id} />
          </div>
        ) : section === "lineage" ? (
          <ProjectLineage projectId={project.id} />
        ) : section === "ancestry" ? (
          <GraphSuspense>
            <ProjectAncestryView projectId={project.id} />
          </GraphSuspense>
        ) : (
          <div className="h-full overflow-y-auto">
            <TrackerManager projectId={project.id} projectName={project.Name} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ProvenanceTab
